import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { useGetList, useGetOne, useRecordContext } from "react-admin";
import { StudentTab } from "./StudentTab";
import { autovioColors, overviewCardStyle } from "../../misc/backofficeTheme";
import { Student } from "../../providers/studentsProvider";
import { DateWithoutTime } from "../../model/DateWithoutTime";
import { DateTime } from "luxon";
import { ReactElement, type ReactNode, useCallback, useMemo, useState } from "react";
import { AutovioCalendarEvent } from "../../model/autovioCalendarEvents";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import { Column } from "../../misc/Column";
import { calendarEventColor, calendarEventTitle } from "../../utils/calendar";
import { Row } from "../../misc/Row";
import { HeaderField } from "../../misc/HeaderField";
import { useDialog } from "../../hooks/useDialog";
import { StudentDayDialog } from "../../dialogs/StudentDayDialog";
import { StudentAttendanceLogic } from "../../model/StudentAttendanceLogic";
import { useMenu } from "../../hooks/useMenu";
import { serverAPI } from "../../api/server.api";

export function StudentAttendanceTab() {
  const student = useRecordContext<Student>();
  const studentId = student.id;
  const [selectedEvents, setSelectedEvents] = useState<Array<AutovioCalendarEvent>>([]);
  const { dialogProps, openDialog } = useDialog();
  const { data: bookedEvents } = useGetList<AutovioCalendarEvent>("calendarEvents", {
    filter: {
      studentId,
      drivingSchoolId: student.drivingSchoolId,
      __record__: (event: AutovioCalendarEvent) => !event.deleted && StudentAttendanceLogic.booked(event, studentId),
    },
    sort: { field: "start", order: "ASC" },
    pagination: { page: 1, perPage: 9999 },
    meta: { includeDeleted: true },
  });

  const openEventsDialog = useCallback((events: Array<AutovioCalendarEvent>) => {
    setSelectedEvents(events);
    openDialog();
  }, []);

  if (!bookedEvents) {
    return <LoadingIndicator />;
  }

  return (
    <StudentTab>
      <Column gap="20px">
        <Box sx={{ ...overviewCardStyle, padding: "20px 30px" }}>
          <_Overview studentId={studentId} bookedEvents={bookedEvents} />
        </Box>
        {bookedEvents.length ? (
          <Box sx={{ ...overviewCardStyle, maxWidth: "100%", minWidth: 0 }}>
            <Box
              data-testid="StudentAttendanceTable-scrollArea"
              sx={{
                containerType: "inline-size",
                maxWidth: "100%",
                minWidth: 0,
                overflowX: "auto",
                "@container (max-width: 599px)": {
                  "& .StudentAttendanceTable td": { padding: "3px" },
                },
              }}
            >
              <table className="StudentAttendanceTable" style={{ minWidth: "600px" }}>
                <_TableHeader />
                <_TableBody student={student} bookedEvents={bookedEvents} openEventsDialog={openEventsDialog} />
              </table>
            </Box>
            {<StudentDayDialog studentId={studentId} events={selectedEvents} {...dialogProps} />}
          </Box>
        ) : null}
      </Column>
    </StudentTab>
  );
}

function _Overview(props: { studentId: string; bookedEvents: Array<AutovioCalendarEvent> }) {
  const { studentId, bookedEvents } = props;
  const { numDaysAttendedLesson, numDaysMissedLesson } = useMemo(() => {
    const daysAttendedLesson = new Set<number>();
    const daysMissedLesson = new Set<number>();
    for (const event of bookedEvents) {
      const day = DateWithoutTime.fromDateTime(event.start).valueOf();
      if (StudentAttendanceLogic.attended(event, studentId)) {
        daysAttendedLesson.add(day);
      } else if (StudentAttendanceLogic.canceledOrNoShow(event, studentId)) {
        daysMissedLesson.add(day);
      }
    }
    return { numDaysAttendedLesson: daysAttendedLesson.size, numDaysMissedLesson: daysMissedLesson.size };
  }, [bookedEvents]);

  return (
    <Row gap="40px">
      <HeaderField label="Anwesend">
        <Typography sx={{ fontWeight: 600, fontSize: "15px" }}>
          {numDaysAttendedLesson === 1 ? "1 Tag" : `${numDaysAttendedLesson} Tage`}
        </Typography>
      </HeaderField>
      <HeaderField label="Abwesend">
        <Typography sx={{ fontWeight: 600, fontSize: "15px" }}>
          {numDaysMissedLesson === 1 ? "1 Tag" : `${numDaysMissedLesson} Tage`}
        </Typography>
      </HeaderField>
    </Row>
  );
}

function _TableHeader() {
  return (
    <thead>
      <tr>{Array.from({ length: 32 }).map((_, i) => (i === 0 ? <th key={i} /> : <th key={i}>{i}</th>))}</tr>
    </thead>
  );
}

function _TableBody(props: {
  student: Student;
  bookedEvents: Array<AutovioCalendarEvent>;
  openEventsDialog: (events: Array<AutovioCalendarEvent>) => void;
}) {
  const { student, bookedEvents, openEventsDialog } = props;
  const studentId = student.id;
  const { data: drivingSchool } = useGetOne("drivingSchools", { id: student.drivingSchoolId });
  const showMoreMenuButton = useMemo(() => {
    return drivingSchool?.customizations.includes("supportForTheEmploymentAgency") ?? false;
  }, [drivingSchool]);
  const rows = useMemo(() => {
    const bookedEventsByDate = new Map<number, Array<AutovioCalendarEvent>>();
    for (const event of bookedEvents ?? []) {
      const key = DateWithoutTime.fromDateTime(event.start).valueOf();
      const bookedEventsOfDay = bookedEventsByDate.get(key);
      if (bookedEventsOfDay) {
        bookedEventsOfDay.push(event);
      } else {
        bookedEventsByDate.set(key, [event]);
      }
    }
    const today = DateWithoutTime.today();
    const today_ = today.valueOf();

    function renderCell(date: DateWithoutTime, bookedEvents: undefined | Array<AutovioCalendarEvent>): ReactNode {
      if (!bookedEvents) {
        return <div className="empty" />;
      }
      const colors = new Set(bookedEvents.map((it) => _color(it, studentId)));
      return (
        <Tooltip
          arrow
          placement={date.day <= 13 ? "right-start" : "left-start"}
          title={
            bookedEvents.length === 1 ? (
              <_Tooltip event={bookedEvents[0]} />
            ) : (
              <Column>
                {bookedEvents.map((it) => (
                  <_Tooltip key={it.id} event={it} />
                ))}
              </Column>
            )
          }
        >
          <div
            onClick={() => openEventsDialog(bookedEvents)}
            style={
              colors.size === 1
                ? { backgroundColor: colors.values().next().value }
                : {
                    display: "flex",
                    flexDirection: "column",
                  }
            }
          >
            {colors.size > 1 &&
              Array.from(colors).map((color, index) => (
                <div
                  key={color}
                  style={{
                    flex: 1,
                    backgroundColor: color,
                    ...(index === 0
                      ? { borderRadius: "3px 3px 0 0" }
                      : index === colors.size - 1
                        ? { borderRadius: "0 0 3px 3px" }
                        : {}),
                  }}
                />
              ))}
          </div>
        </Tooltip>
      );
    }

    function* renderRowCells(year: number, month: number): Iterable<ReactElement> {
      let date = new DateWithoutTime(year, month, 1);
      for (;;) {
        const key = date.valueOf();
        const bookedEvents = bookedEventsByDate.get(key);
        yield <td key={key}>{renderCell(date, bookedEvents)}</td>;
        if (key === today_) {
          break;
        }
        const nextDay = date.nextDay;
        if (nextDay.month === month) {
          date = nextDay;
        } else {
          break;
        }
      }
      for (let i = date.day + 1; i <= 31; ++i) {
        yield <td key={i} />;
      }
    }

    function* renderRows(): Iterable<ReactElement> {
      const firstDate = bookedEvents[0].start;
      const lastDate = bookedEvents.at(-1)!.start;
      let { year, month } = lastDate;
      while (year > firstDate.year || (year === firstDate.year && month >= firstDate.month)) {
        yield (
          <tr key={`${year}-${month}`}>
            <_TableRowHeader showMoreMenuButton={showMoreMenuButton} studentId={studentId} year={year} month={month} />
            {Array.from(renderRowCells(year, month))}
          </tr>
        );
        if (month === 1) {
          month = 12;
          year -= 1;
        } else {
          month -= 1;
        }
      }
    }

    return Array.from(renderRows());
  }, [studentId, bookedEvents]);

  return <tbody>{rows}</tbody>;
}

function _TableRowHeader({
  showMoreMenuButton,
  studentId,
  year,
  month,
}: {
  showMoreMenuButton: boolean;
  studentId: string;
  year: number;
  month: number;
}) {
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const hideMoreMenuButton = useCallback(() => {
    setShowMoreMenu(false);
  }, []);

  return (
    <th
      style={{ position: "relative" }}
      {...(showMoreMenuButton && {
        onMouseEnter: () => setShowMoreMenu(true),
        onMouseLeave: hideMoreMenuButton,
      })}
    >
      {DateTime.fromObject({ year, month, day: 1 }, { zone: "Europe/Berlin", locale: "de" }).toFormat("LLLL yyyy")}
      {showMoreMenu && (
        <_RowHeaderMoreMenu studentId={studentId} year={year} month={month} hideMoreMenuButton={hideMoreMenuButton} />
      )}
    </th>
  );
}
function _RowHeaderMoreMenu({
  studentId,
  year,
  month,
  hideMoreMenuButton,
}: {
  studentId: string;
  year: number;
  month: number;
  hideMoreMenuButton: () => void;
}) {
  const { anchorEl, open, openMenu, closeMenu } = useMenu();
  const [isLoading, setIsLoading] = useState(false);
  const downloadMonthlyAbsenceReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const pdf = await serverAPI.downloadStudentMonthlyAbsenceReport(studentId, year.toString(), month.toString());
      const blob = new Blob([pdf], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SGB_III_Bescheinigung_Fehlzeiten_${year}_${month < 10 ? "0" : ""}${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showSuccessToast("Die SGB III - FbW Bescheinigung wurde erfolgreich erstellt");
    } catch (error) {
      console.error(error);
      showErrorToast("Die SGB III - FbW Bescheinigung konnte nicht erstellt werden");
    } finally {
      setIsLoading(false);
    }
    closeMenu();
    hideMoreMenuButton();
  }, [studentId, year, month]);

  return (
    <>
      <IconButton
        size="small"
        color="default"
        onClick={openMenu}
        sx={{
          position: "absolute",
          right: "-10px",
          top: "2px",
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={closeMenu}
        sx={{ "& .MuiList-root": { minWidth: "320px", minHeight: "40px" } }}
      >
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <MenuItem onClick={downloadMonthlyAbsenceReport}>SGB III - FbW Bescheinigung erstellen</MenuItem>
        )}
      </Menu>
    </>
  );
}

function _Tooltip({ event }: { event: AutovioCalendarEvent }) {
  return (
    <Column>
      {event.start.toFormat("HH:mm") + "\u00A0Uhr"}
      <Typography variant="body2" fontWeight="bold" noWrap>
        {calendarEventTitle(event)}
      </Typography>
    </Column>
  );
}

function _color(event: AutovioCalendarEvent, studentId: string): string {
  if (StudentAttendanceLogic.attended(event, studentId)) {
    return calendarEventColor(event);
  } else if (StudentAttendanceLogic.canceledOrNoShow(event, studentId)) {
    return autovioColors.red;
  } else {
    return autovioColors.grey;
  }
}
